import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from '@mui/material';
import {
  FormSectionTitle,
  FormSection,
  FormSubsection,
  FormSectionSubtitle
} from './form-input';
import {
  ColorPickerField,
  SwitchField,
  AlignmentField,
  SelectField,
  SliderField,
  TextField,
  CustomIconField,
  HeaderImageField,
  LanguageField,
  JsField,
  SpacingField
} from './fields';
import {
  ButtonCallToAction,
  Features,
  isNotNullOrUndefined,
  SizeImpactReminder,
  useErrorNotification,
  WidgetConfiguration,
  RoleContext,
  openInNewTab
} from '../framework';
import { Line } from './styled';
import { ButtonMode, useButtonMode } from './hooks/use-button-mode';
import { RoundedCornersField } from './fields/rounded-corners-field';
import { icons } from './fields/custom-icon-field';

const defaultButtonCta: ButtonCallToAction = {
  enabled: false,
  delay: 2000,
  showAfterPageLoads: 2,
  showOnlyOnce: false,
  alignment: 'auto'
};

const defaultSizeImpactReminder: SizeImpactReminder = {
  enabled: false,
  showWhenCartDiffers: true,
  showWhenResultDiffers: true,
  alignment: 'auto'
};

export interface WidgetConfigurationBasicProps {
  widgetConfiguration: WidgetConfiguration;
  updateWidgetConfiguration: (value: WidgetConfiguration) => void;
  retailerSlug: string;
  hideTitle?: boolean;
  externallyChanged?: WidgetConfiguration;
  features?: Features;
}

export function WidgetConfigurationBasic(props: WidgetConfigurationBasicProps) {
  const {
    widgetConfiguration,
    updateWidgetConfiguration,
    retailerSlug,
    hideTitle,
    externallyChanged,
    features
  } = props;

  const { isAdmin } = useContext(RoleContext);

  const { t } = useTranslation();
  const { notification } = useErrorNotification();

  const buttonMode = useButtonMode(widgetConfiguration);

  const handleButtonlessUpsell = () =>
    openInNewTab(
      `mailto:sales@faslet.me?subject=Enable Buttonless Mode&body=I'd like to enable Buttonless mode in my store`
    );

  const changeButtonMode = (newMode: ButtonMode) => {
    switch (newMode) {
      case 'buttonLessMode':
        if (isAdmin()) {
          updateWidgetConfiguration({
            ...widgetConfiguration,
            buttonless: true
          });
        } else {
          handleButtonlessUpsell();
        }
        break;

      case 'buttonAndResultMode':
        updateWidgetConfiguration({
          ...widgetConfiguration,
          buttonFillParent: true,
          showButtonAndResult: true,
          buttonless: false
        });
        break;

      case 'fitTextMode':
        updateWidgetConfiguration({
          ...widgetConfiguration,
          buttonFillParent: false,
          showButtonAndResult: false,
          buttonless: false
        });
        break;

      case 'fillParentMode':
        updateWidgetConfiguration({
          ...widgetConfiguration,
          buttonFillParent: true,
          showButtonAndResult: false,
          buttonless: false
        });
        break;

      default:
        break;
    }
  };

  const { customIcon, disableIcon } = widgetConfiguration;

  const showIconColor = useMemo(() => {
    if (disableIcon) return false;

    const isStandardIcon = icons.some(icon => customIcon === icon.url);

    return isStandardIcon;
  }, [customIcon, disableIcon]);

  return (
    <>
      {!hideTitle && (
        <FormSectionTitle>{t('widgetConfiguration')}</FormSectionTitle>
      )}
      <FormSection>
        <FormSectionSubtitle>{t('general')}</FormSectionSubtitle>

        <TextField
          label={t('fontFace')}
          highlighted={isNotNullOrUndefined(externallyChanged?.font)}
          value={widgetConfiguration.font ?? ''}
          onChange={value =>
            updateWidgetConfiguration({ ...widgetConfiguration, font: value })
          }
        />

        <ColorPickerField
          label={t('primaryColor')}
          highlighted={isNotNullOrUndefined(externallyChanged?.colorPrimary)}
          value={widgetConfiguration.colorPrimary}
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              colorPrimary: value
            })
          }
        />

        <ColorPickerField
          label={t('secondaryColor')}
          highlighted={isNotNullOrUndefined(externallyChanged?.colorSecondary)}
          value={widgetConfiguration.colorSecondary}
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              colorSecondary: value
            })
          }
        />

        <ColorPickerField
          label={t('fontColor')}
          highlighted={isNotNullOrUndefined(externallyChanged?.fontColor)}
          value={widgetConfiguration.fontColor}
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              fontColor: value
            })
          }
        />

        <ColorPickerField
          label={t('fontColorSecondary')}
          highlighted={isNotNullOrUndefined(
            externallyChanged?.fontColorSecondary
          )}
          value={widgetConfiguration.fontColorSecondary}
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              fontColorSecondary: value
            })
          }
        />

        <ColorPickerField
          label={t('bodyFontColor')}
          highlighted={isNotNullOrUndefined(externallyChanged?.bodyFontColor)}
          value={widgetConfiguration.bodyFontColor ?? '#666459'}
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              bodyFontColor: value
            })
          }
        />

        <ColorPickerField
          label={t('addToCartButtonColor')}
          highlighted={isNotNullOrUndefined(
            externallyChanged?.addToCartButtonColor
          )}
          value={
            widgetConfiguration.addToCartButtonColor ??
            widgetConfiguration.colorPrimary ??
            '#50aa8d'
          }
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              addToCartButtonColor: value
            })
          }
        />

        <ColorPickerField
          label={t('addToCartFontColor')}
          highlighted={isNotNullOrUndefined(
            externallyChanged?.addToCartFontColor
          )}
          value={
            widgetConfiguration.addToCartFontColor ??
            widgetConfiguration.fontColor ??
            '#50aa8d'
          }
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              addToCartFontColor: value
            })
          }
        />

        <ColorPickerField
          label={t('headerFontColor')}
          highlighted={isNotNullOrUndefined(externallyChanged?.headerFontColor)}
          value={widgetConfiguration.headerFontColor}
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              headerFontColor: value
            })
          }
        />

        {isAdmin() && showIconColor && (
          <ColorPickerField
            label={t('fIconColor')}
            highlighted={isNotNullOrUndefined(externallyChanged?.fIconColor)}
            value={widgetConfiguration.fIconColor}
            onChange={value =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                fIconColor: value
              })
            }
          />
        )}

        <RoundedCornersField
          label={t('roundedCorners')}
          highlighted={
            isNotNullOrUndefined(externallyChanged?.borderRadius) ||
            isNotNullOrUndefined(externallyChanged?.popupBorderRadius)
          }
          value={widgetConfiguration}
          onChange={value => {
            updateWidgetConfiguration({
              ...widgetConfiguration,
              ...value
            });
          }}
        />

        {isAdmin() && (
          <SwitchField
            label={t('blockEventsFromHiddenWidget')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.blockEventsFromHiddenWidget
            )}
            value={widgetConfiguration.blockEventsFromHiddenWidget ?? false}
            onChange={checked =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                blockEventsFromHiddenWidget: checked
              })
            }
          />
        )}

        <LanguageField
          label={t('defaultLanguage')}
          highlighted={isNotNullOrUndefined(externallyChanged?.locale)}
          value={widgetConfiguration.locale ?? 'autoDetect'}
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              locale: value !== 'autoDetect' ? value : undefined
            })
          }
          disabled={!features?.languageChoice && !isAdmin()}
        />
      </FormSection>
      <FormSection>
        <FormSectionSubtitle>{t('button')}</FormSectionSubtitle>{' '}
        {buttonMode !== 'buttonLessMode' && (
          <>
            <SliderField
              label={t('buttonFontSize')}
              highlighted={isNotNullOrUndefined(
                externallyChanged?.buttonFontSize
              )}
              value={widgetConfiguration.buttonFontSize ?? 16}
              onChange={value =>
                updateWidgetConfiguration({
                  ...widgetConfiguration,
                  buttonFontSize: value
                })
              }
              step={1}
              min={8}
              max={32}
              marks={[
                { value: 8, label: 8 },
                { value: 16, label: 16 },
                { value: 24, label: 24 },
                { value: 32, label: 32 }
              ]}
            />

            <SliderField
              label={t('buttonHeight')}
              highlighted={isNotNullOrUndefined(
                externallyChanged?.buttonHeight
              )}
              value={widgetConfiguration.buttonHeight ?? 32}
              onChange={value =>
                updateWidgetConfiguration({
                  ...widgetConfiguration,
                  buttonHeight: value
                })
              }
              step={1}
              min={20}
              max={100}
              marks={[
                { value: 20, label: 20 },
                { value: 40, label: 40 },
                { value: 60, label: 60 },
                { value: 80, label: 80 },
                { value: 100, label: 100 }
              ]}
            />

            <SwitchField
              label={t('buttonBackgroundColor')}
              highlighted={isNotNullOrUndefined(
                externallyChanged?.buttonBackgroundColor
              )}
              value={widgetConfiguration.buttonBackgroundColor == '#f0f0f0ff'}
              onChange={value =>
                updateWidgetConfiguration({
                  ...widgetConfiguration,
                  buttonBackgroundColor: value ? 'f0f0f0ff' : '#f0f0f000'
                })
              }
            />
          </>
        )}
        <SelectField
          label={t('buttonMode')}
          value={buttonMode}
          onChange={value => changeButtonMode(value as ButtonMode)}
          options={[
            {
              value: 'fitTextMode',
              label: t('fitTextMode')
            },
            {
              value: 'fillParentMode',
              label: t('fillParentMode')
            },
            {
              value: 'buttonAndResultMode',
              label: t('buttonAndResultMode')
            },
            {
              value: 'buttonLessMode',
              label: t('buttonLessMode')
            }
          ]}
          disabled={!isAdmin() && buttonMode === 'buttonLessMode'}
          highlighted={
            isNotNullOrUndefined(externallyChanged?.buttonFillParent) ||
            isNotNullOrUndefined(externallyChanged?.showButtonAndResult) ||
            isNotNullOrUndefined(externallyChanged?.buttonless)
          }
        />
        {buttonMode !== 'buttonLessMode' && (
          <AlignmentField
            label={t(
              widgetConfiguration?.buttonFillParent &&
                !widgetConfiguration?.showButtonAndResult
                ? 'buttonContentAlignment'
                : 'buttonAlignmentInParent'
            )}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.buttonFillParent
                ? externallyChanged?.buttonContentAlignment
                : externallyChanged?.buttonAlignmentInParent
            )}
            value={
              widgetConfiguration.buttonFillParent
                ? widgetConfiguration.buttonContentAlignment ?? 'center'
                : widgetConfiguration.buttonAlignmentInParent ?? 'left'
            }
            onChange={value => {
              if (widgetConfiguration.buttonFillParent) {
                updateWidgetConfiguration({
                  ...widgetConfiguration,
                  buttonContentAlignment: value
                });
              } else {
                updateWidgetConfiguration({
                  ...widgetConfiguration,
                  buttonAlignmentInParent: value
                });
              }
            }}
          />
        )}
        {buttonMode !== 'buttonLessMode' && !features?.uiRefresh2024 && (
          <SwitchField
            label={t('invertButtonColors')}
            highlighted={isNotNullOrUndefined(externallyChanged?.invertButton)}
            value={widgetConfiguration.invertButton ?? false}
            onChange={checked =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                invertButton: checked
              })
            }
          />
        )}
        {isAdmin() && buttonMode !== 'buttonLessMode' && (
          <CustomIconField
            retailerSlug={retailerSlug}
            label={t('customIcon')}
            value={widgetConfiguration}
            onChange={value => {
              updateWidgetConfiguration({
                ...widgetConfiguration,
                ...value
              });
            }}
            highlighted={isNotNullOrUndefined(externallyChanged?.customIcon)}
          />
        )}
        <FormSubsection
          hideBorder={!widgetConfiguration.buttonCallToAction?.enabled}
        >
          <SwitchField
            label={t('buttonCallToAction')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.buttonCallToAction?.enabled
            )}
            value={widgetConfiguration.buttonCallToAction?.enabled ?? false}
            onChange={checked => {
              updateWidgetConfiguration({
                ...widgetConfiguration,
                buttonCallToAction: {
                  ...defaultButtonCta,
                  ...widgetConfiguration.buttonCallToAction,
                  enabled: checked
                }
              });
            }}
          />
          <Collapse in={widgetConfiguration.buttonCallToAction?.enabled}>
            <FormSection>
              <SliderField
                label={t('buttonCallToActionPageDelay')}
                highlighted={isNotNullOrUndefined(
                  externallyChanged?.buttonCallToAction?.delay
                )}
                value={widgetConfiguration.buttonCallToAction?.delay ?? 2000}
                onChange={value => {
                  updateWidgetConfiguration({
                    ...widgetConfiguration,
                    buttonCallToAction: {
                      ...(widgetConfiguration.buttonCallToAction ??
                        defaultButtonCta),
                      delay: value
                    }
                  });
                }}
                min={0}
                max={10000}
                step={500}
                marks={[
                  { value: 0, label: '0' },
                  { value: 2000, label: '2s' },
                  { value: 5000, label: '5s' },
                  { value: 10000, label: '10s' }
                ]}
              />
              <SelectField
                label={t('buttonCallToActionPageLoads')}
                highlighted={isNotNullOrUndefined(
                  externallyChanged?.buttonCallToAction?.showAfterPageLoads
                )}
                value={
                  widgetConfiguration.buttonCallToAction?.showAfterPageLoads ??
                  0
                }
                onChange={value => {
                  updateWidgetConfiguration({
                    ...widgetConfiguration,
                    buttonCallToAction: {
                      ...defaultButtonCta,
                      ...widgetConfiguration.buttonCallToAction,
                      showAfterPageLoads: Number(value)
                    }
                  });
                }}
                options={[
                  { value: 0, label: '0' },
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                  { value: 5, label: '5' }
                ]}
              />
              <SwitchField
                label={t('buttonCallToActionShowOnlyOnce')}
                highlighted={isNotNullOrUndefined(
                  externallyChanged?.buttonCallToAction?.showOnlyOnce
                )}
                value={
                  widgetConfiguration.buttonCallToAction?.showOnlyOnce ?? false
                }
                onChange={checked => {
                  updateWidgetConfiguration({
                    ...widgetConfiguration,
                    buttonCallToAction: {
                      ...defaultButtonCta,
                      ...widgetConfiguration.buttonCallToAction,
                      showOnlyOnce: checked
                    }
                  });
                }}
              />
              <AlignmentField
                label={t('buttonCallToActionAlignment')}
                includeAuto
                value={
                  widgetConfiguration.buttonCallToAction?.alignment ?? 'auto'
                }
                onChange={value => {
                  updateWidgetConfiguration({
                    ...widgetConfiguration,
                    buttonCallToAction: {
                      ...defaultButtonCta,
                      ...widgetConfiguration.buttonCallToAction,
                      alignment: value
                    }
                  });
                }}
                highlighted={isNotNullOrUndefined(
                  externallyChanged?.buttonCallToAction?.alignment
                )}
              />
            </FormSection>
          </Collapse>
        </FormSubsection>
        {isAdmin() && (
          <FormSubsection
            hideBorder={!widgetConfiguration.sizeImpactReminder?.enabled}
          >
            <SwitchField
              label={t('sizeImpactReminder')}
              highlighted={isNotNullOrUndefined(
                externallyChanged?.sizeImpactReminder?.enabled
              )}
              value={widgetConfiguration.sizeImpactReminder?.enabled ?? false}
              onChange={checked => {
                updateWidgetConfiguration({
                  ...widgetConfiguration,
                  sizeImpactReminder: {
                    ...defaultSizeImpactReminder,
                    ...widgetConfiguration.sizeImpactReminder,
                    enabled: checked
                  }
                });
              }}
            />
            <Collapse in={widgetConfiguration.sizeImpactReminder?.enabled}>
              <FormSection>
                <SwitchField
                  label={t('sizeImpactReminderWhenCartDiffers')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.sizeImpactReminder?.showWhenCartDiffers
                  )}
                  value={
                    widgetConfiguration.sizeImpactReminder
                      ?.showWhenCartDiffers ?? true
                  }
                  onChange={checked =>
                    updateWidgetConfiguration({
                      ...widgetConfiguration,
                      sizeImpactReminder: {
                        ...defaultSizeImpactReminder,
                        ...widgetConfiguration.sizeImpactReminder,
                        showWhenCartDiffers: checked
                      }
                    })
                  }
                />
                <SwitchField
                  label={t('sizeImpactReminderWhenResultDiffers')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.sizeImpactReminder?.showWhenResultDiffers
                  )}
                  value={
                    widgetConfiguration.sizeImpactReminder
                      ?.showWhenResultDiffers ?? true
                  }
                  onChange={checked =>
                    updateWidgetConfiguration({
                      ...widgetConfiguration,
                      sizeImpactReminder: {
                        ...defaultSizeImpactReminder,
                        ...widgetConfiguration.sizeImpactReminder,
                        showWhenResultDiffers: checked
                      }
                    })
                  }
                />
                <AlignmentField
                  label={t('sizeImpactReminderAlignment')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.sizeImpactReminder?.alignment
                  )}
                  includeAuto
                  value={
                    widgetConfiguration.sizeImpactReminder?.alignment ?? 'auto'
                  }
                  onChange={value =>
                    updateWidgetConfiguration({
                      ...widgetConfiguration,
                      sizeImpactReminder: {
                        ...defaultSizeImpactReminder,
                        ...widgetConfiguration.sizeImpactReminder,
                        alignment: value
                      }
                    })
                  }
                />
                {isAdmin() && (
                  <JsField
                    label={t('variantJs')}
                    value={widgetConfiguration.sizeImpactReminder?.variantJs}
                    onChange={value => {
                      updateWidgetConfiguration({
                        ...widgetConfiguration,
                        sizeImpactReminder: {
                          ...defaultSizeImpactReminder,
                          ...widgetConfiguration.sizeImpactReminder,
                          variantJs: value
                        }
                      });
                    }}
                  />
                )}
                {isAdmin() && (
                  <JsField
                    label={t('cartJs')}
                    value={widgetConfiguration.sizeImpactReminder?.cartJs}
                    onChange={value => {
                      updateWidgetConfiguration({
                        ...widgetConfiguration,
                        sizeImpactReminder: {
                          ...defaultSizeImpactReminder,
                          ...widgetConfiguration.sizeImpactReminder,
                          cartJs: value
                        }
                      });
                    }}
                  />
                )}
              </FormSection>
            </Collapse>
          </FormSubsection>
        )}
        {buttonMode !== 'buttonLessMode' && (
          <SpacingField
            value={widgetConfiguration.spacing ?? '0px'}
            onChange={value =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                spacing: value
              })
            }
            highlighted={isNotNullOrUndefined(externallyChanged?.spacing)}
          />
        )}
        {isAdmin() && (
          <TextField
            label={t('buttonParentSelector')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.buttonParentSelector
            )}
            value={widgetConfiguration.buttonParentSelector ?? ''}
            onChange={value =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                buttonParentSelector: value
              })
            }
          />
        )}
        {isAdmin() && (
          <TextField
            label={t('buttonCssClass')}
            highlighted={isNotNullOrUndefined(externallyChanged?.buttonClass)}
            value={widgetConfiguration.buttonClass ?? ''}
            onChange={value =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                buttonClass: value
              })
            }
          />
        )}
        {isAdmin() && (
          <TextField
            label={t('buttonStylesheet')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.buttonStylesheet
            )}
            value={widgetConfiguration.buttonStylesheet ?? ''}
            onChange={value =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                buttonStylesheet: value
              })
            }
          />
        )}
      </FormSection>
      <FormSection hideBorder>
        <FormSectionSubtitle>{t('popup')}</FormSectionSubtitle>

        <HeaderImageField
          retailerSlug={retailerSlug}
          value={widgetConfiguration.headerImage}
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              headerImage: value
            })
          }
          highlighted={isNotNullOrUndefined(externallyChanged?.headerImage)}
        />

        {!features?.uiRefresh2024 && (
          <SwitchField
            label={t('disableBorder')}
            highlighted={isNotNullOrUndefined(externallyChanged?.disableBorder)}
            value={widgetConfiguration.disableBorder ?? false}
            onChange={checked =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                disableBorder: checked
              })
            }
          />
        )}

        <SwitchField
          label={t('disableUiShadows')}
          highlighted={isNotNullOrUndefined(
            externallyChanged?.disableUiShadows
          )}
          value={widgetConfiguration.disableUiShadows ?? false}
          onChange={checked =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              disableUiShadows: checked
            })
          }
        />

        <SwitchField
          label={t('disableBackgroundBlur')}
          highlighted={isNotNullOrUndefined(
            externallyChanged?.disableBackgroundBlur
          )}
          value={widgetConfiguration.disableBackgroundBlur ?? false}
          onChange={checked =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              disableBackgroundBlur: checked
            })
          }
        />

        <SliderField
          label={t('productImagePadding')}
          highlighted={isNotNullOrUndefined(
            externallyChanged?.productImagePadding
          )}
          value={widgetConfiguration.productImagePadding ?? 0}
          onChange={value =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              productImagePadding: value
            })
          }
          min={0}
          max={20}
          step={1}
          marks={[
            { value: 0, label: '0' },
            { value: 5, label: '5' },
            { value: 10, label: '10' },
            { value: 20, label: '20' }
          ]}
        />

        <SwitchField
          label={t('disableUnitSelector')}
          highlighted={isNotNullOrUndefined(
            externallyChanged?.disableUnitSelector
          )}
          value={widgetConfiguration.disableUnitSelector ?? false}
          onChange={checked =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              disableUnitSelector: checked
            })
          }
        />

        <SwitchField
          label={t('disableBackToTop')}
          highlighted={isNotNullOrUndefined(
            externallyChanged?.disableBackToTop
          )}
          value={widgetConfiguration.disableBackToTop ?? false}
          onChange={checked =>
            updateWidgetConfiguration({
              ...widgetConfiguration,
              disableBackToTop: checked
            })
          }
        />

        {isAdmin() && (
          <SwitchField
            label={t('fasletOutlink')}
            highlighted={isNotNullOrUndefined(externallyChanged?.fasletOutlink)}
            value={widgetConfiguration.fasletOutlink ?? true}
            onChange={checked =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                fasletOutlink: checked
              })
            }
          />
        )}

        {isAdmin() && (
          <SwitchField
            label={t('disableAddToCart')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.disableAddToCart
            )}
            value={widgetConfiguration.disableAddToCart ?? false}
            onChange={checked =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                disableAddToCart: checked
              })
            }
          />
        )}
      </FormSection>
      <Line />
      {isAdmin() && (
        <FormSection>
          <FormSectionSubtitle>{t('specialOptions')}</FormSectionSubtitle>
          <SwitchField
            label={t('chatMode')}
            highlighted={isNotNullOrUndefined(externallyChanged?.chatMode)}
            value={widgetConfiguration.chatMode ?? false}
            onChange={checked =>
              updateWidgetConfiguration({
                ...widgetConfiguration,
                chatMode: checked
              })
            }
          />
        </FormSection>
      )}
      {notification}
    </>
  );
}

export default WidgetConfigurationBasic;
